import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ZoomIn, Home as HomeIcon, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isLogoZoomOpen, setIsLogoZoomOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Detect active section only on home page
      if (location.pathname === '/') {
        const sections = ['services', 'specializations', 'why-choose', 'news', 'contact'];
        const scrollPosition = window.scrollY + 200;

        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              setActiveSection(section);
              return;
            }
          }
        }
        setActiveSection('hero');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const scrollToSection = (id) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navigateToPage = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
  };

  const isActive = (section) => {
    if (section === 'firm-profile') return location.pathname === '/firm-profile';
    if (section === 'careers') return location.pathname === '/careers';
    if (section === 'services') return location.pathname === '/services';
    if (section === 'publications') return location.pathname === '/publications';
    if (section === 'team') return location.pathname === '/team';
    if (location.pathname !== '/') return false;
    return activeSection === section;
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-400 ${
      isScrolled ? 'bg-white/70 backdrop-blur-xl border-b border-black/5 shadow-sm' : 'bg-white/70 backdrop-blur-xl'
    }`}>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-28 md:h-32">
          {/* Logo */}
          <button
            type="button"
            className="group relative flex items-center shrink-0 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            onClick={() => setIsLogoZoomOpen(true)}
            data-testid="header-logo"
            title="Click to zoom · R S T & Co. Chartered Accountants"
            aria-label="Open logo zoom view"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/ckmcku7b_RST%20Logo%20for%20Website.png"
              alt="RST & Co. Chartered Accountants"
              className="h-16 md:h-20 xl:h-24 w-auto shrink-0 transition-transform duration-300 group-hover:scale-105"
              data-testid="header-logo-img"
            />
            <span
              className="absolute -bottom-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gray-900 text-white rounded-full p-1.5 shadow-md"
              aria-hidden="true"
            >
              <ZoomIn size={12} />
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5">
            <button 
              onClick={() => navigateToPage('/services')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('services') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('specializations')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('specializations') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-expertise"
            >
              Expertise
            </button>
            <button 
              onClick={() => scrollToSection('why-choose')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('why-choose') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-why-us"
            >
              Why Us
            </button>
            <button 
              onClick={() => scrollToSection('news')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('news') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-news"
            >
              News Update
            </button>
            <button 
              onClick={() => scrollToSection('contact')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 ${
                isActive('contact') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-contact"
            >
              Contact
            </button>
            
            {/* Separator */}
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            
            <button 
              onClick={() => navigateToPage('/publications')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('publications') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-publications"
            >
              Publications
            </button>
            <button 
              onClick={() => navigateToPage('/careers')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('careers') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-careers"
            >
              Careers
            </button>
            <button 
              onClick={() => navigateToPage('/firm-profile')} 
              className={`font-body text-sm font-medium px-2 py-2 transition-all duration-300 whitespace-nowrap ${
                isActive('firm-profile') 
                  ? 'text-brand-blue bg-brand-blue/5' 
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid="nav-firm-profile"
            >
              Profile
            </button>
            
            <Button 
              onClick={() => scrollToSection('cta')}
              className="ml-2 bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-4 py-2 h-auto text-sm transition-all duration-300 whitespace-nowrap"
              data-testid="nav-book-consultation"
            >
              Book Consultation
            </Button>

            {/* Admin button — small, top-right */}
            <button
              type="button"
              onClick={() =>
                navigate(auth?.user ? '/team' : '/admin/login')
              }
              className="ml-1.5 flex items-center justify-center w-9 h-9 shrink-0 border border-gray-300 text-gray-600 hover:text-white hover:bg-gray-900 hover:border-gray-900 transition-all duration-200"
              title={auth?.user ? 'Admin Dashboard' : 'Admin Sign-In'}
              aria-label="Admin"
              data-testid="nav-admin-btn"
            >
              <ShieldCheck size={16} />
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-900 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200 py-6">
            <nav className="flex flex-col gap-2">
              <button 
                onClick={() => navigateToPage('/services')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('services') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('specializations')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('specializations') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Expertise
              </button>
              <button 
                onClick={() => scrollToSection('why-choose')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('why-choose') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Why Us
              </button>
              <button 
                onClick={() => scrollToSection('news')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('news') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                News Update
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('contact') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Contact
              </button>
              
              <div className="h-px bg-gray-200 my-2"></div>
              
              <button 
                onClick={() => navigateToPage('/publications')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('publications') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Publications
              </button>
              <button 
                onClick={() => navigateToPage('/careers')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('careers') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Careers
              </button>
              <button 
                onClick={() => navigateToPage('/firm-profile')} 
                className={`font-body text-sm font-medium text-left px-4 py-3 transition-colors duration-300 ${
                  isActive('firm-profile') ? 'text-brand-blue bg-brand-blue/5' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Profile
              </button>
              
              <Button 
                onClick={() => scrollToSection('cta')}
                className="mt-4 bg-brand-blue hover:bg-brand-blue-hover text-white font-medium px-6 py-3 transition-all duration-300"
              >
                Book Consultation
              </Button>
            </nav>
          </div>
        )}
      </div>

      {/* Logo Zoom Modal */}
      <Dialog open={isLogoZoomOpen} onOpenChange={setIsLogoZoomOpen}>
        <DialogContent
          className="max-w-3xl bg-white p-0 overflow-hidden border-none"
          data-testid="logo-zoom-modal"
        >
          <DialogTitle className="sr-only">
            RST & Co. Chartered Accountants — Logo
          </DialogTitle>
          <DialogDescription className="sr-only">
            Enlarged view of the RST & Co. Chartered Accountants brand logo.
          </DialogDescription>
          <div className="bg-gray-50 flex items-center justify-center p-10 md:p-16">
            <img
              src="https://customer-assets.emergentagent.com/job_rst-startup-hub/artifacts/ckmcku7b_RST%20Logo%20for%20Website.png"
              alt="RST & Co. Chartered Accountants — Enlarged"
              className="max-h-[70vh] w-auto object-contain"
              data-testid="logo-zoom-img"
            />
          </div>
          <div className="p-6 border-t border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="font-heading text-xl font-bold text-gray-900">
                R S T & Co.
              </p>
              <p className="text-sm text-gray-500" lang="kn">
                ಆರ್ ಎಸ್ ಟಿ ಅಂಡ್ ಕಂ · ಸನ್ನದು ಲೆಕ್ಕ ಪರಿಶೋಧಕರು
              </p>
            </div>
            <Button
              onClick={() => {
                setIsLogoZoomOpen(false);
                navigate('/');
                window.scrollTo(0, 0);
              }}
              className="bg-brand-blue hover:bg-brand-blue-hover text-white font-medium rounded-none"
              data-testid="logo-zoom-home-btn"
            >
              <HomeIcon size={16} className="mr-2" />
              Go to Home
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
